import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { IconCalendarCog } from "@tabler/icons-react"
import { skipToken } from "@reduxjs/toolkit/query/react"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { canManageAppointments } from "@/lib/appointments-access"
import { useAppSelector } from "@/store"
import {
  useGetCalUsersQuery,
  useGetTeamMembersQuery,
  useGetTeamsQuery,
} from "@/app/booking-calendar/bookingCalendarApi"
import { type CalendarDate, SITE_TIME_ZONE, todayInZone } from "@/app/booking-calendar/lib/siteTime"
import "@/app/booking-calendar/bookingCalendar.css"
import type { SlotPlanTarget } from "./availabilityApi"
import { SlotPlanEditor, type SlotPlanWeek } from "./components/SlotPlanEditor"
import { type AvailabilitySelection, availabilitySearch, mondayOf, readAvailabilityParams } from "./lib/weekModel"

/**
 * Appointments → Availability (AB#781): a site's weekly grid of public, private and managed slots, or
 * one host's availability at a site. Anybody in the appointments section reads it; appointments:manage
 * edits it.
 *
 * The choice lives in the URL (`?site=<slug>&view=host&host=<calUserId>`), so a reload keeps it and
 * other pages can link straight to a schedule.
 *
 * The schedule is drawn as a dated week (AB#803), and the week's Monday joins them
 * (`&week=2026-10-19`); without one it opens on the current Pacific week. Overrides are made and
 * withdrawn from the week (AB#806); there is no separate Schedule Overrides page.
 */
export default function AvailabilityPage() {
  const permissions = useAppSelector((state) => state.permissions.permissions)
  const canManage = canManageAppointments(permissions)

  const [params, setParams] = useSearchParams()
  const selection = readAvailabilityParams(params)
  const { site: siteSlug, view, host: calUserId, week: weekParam } = selection
  // Pacific, whatever the browser's zone: "this week" is the office's week.
  const today = todayInZone(SITE_TIME_ZONE)
  const monday: CalendarDate = weekParam ?? mondayOf(today)

  const { data: teams = [], isLoading: teamsLoading } = useGetTeamsQuery()
  const sites = useMemo(
    () => teams.filter(team => !!team.slug).sort((a, b) => a.name.localeCompare(b.name)),
    [teams],
  )
  const site = sites.find(team => team.slug === siteSlug)

  const { data: members = [] } = useGetTeamMembersQuery(view === "host" && site ? { teamId: site.id } : skipToken)
  const { data: calUsers = [] } = useGetCalUsersQuery(view === "host" && site ? undefined : skipToken)
  const hosts = useMemo(() => {
    const byId = new Map(calUsers.map(user => [user.id, user]))
    return members
      .filter(member => member.accepted)
      .map(member => {
        const user = byId.get(member.userId)
        // The email too: an override for this host names them by it (AB#806).
        return { id: member.userId, name: user?.name || user?.email || `Cal user ${member.userId}`, email: user?.email ?? null }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [members, calUsers])

  // Rebuilds the whole query from the merged selection, so the week survives a change of site, view
  // or host: a link always names one schedule in one week.
  function choose(next: Partial<AvailabilitySelection>) {
    setParams(availabilitySearch({ ...selection, ...next }), { replace: true })
  }

  const target: SlotPlanTarget | null = !site?.slug
    ? null
    : view === "site"
      ? { kind: "site", teamSlug: site.slug }
      : calUserId
        ? { kind: "host", teamSlug: site.slug, calUserId }
        : null

  const selectedHost = calUserId ? hosts.find(host => host.id === calUserId) : undefined
  const hostName = selectedHost?.name
  const week: SlotPlanWeek | undefined = site
    ? {
      monday,
      today,
      title: view === "host" && hostName ? `${hostName} at ${site.name}` : site.name,
      onChangeWeek: (next) => choose({ week: next }),
    }
    : undefined

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center gap-2">
        <IconCalendarCog className="size-6" />
        <h1 className="text-2xl font-bold">Availability</h1>
      </div>

      <p className="text-muted-foreground max-w-3xl">
        Each site&apos;s weekly grid of public, private and managed slots, and each host&apos;s weekly availability
        at a site. Breaks are always unavailable.
        {canManage
          ? " Each week shows the dates it covers, with overrides applied. Choose Edit default schedule to change"
            + " the weekly schedule; saved changes are applied to the calendar in the background."
          : " You can view these schedules; changing them needs appointments:manage."}
      </p>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="availability-site">Site</Label>
            <Select value={site?.slug ?? ""} onValueChange={(slug) => choose({ site: slug, host: null })}>
              <SelectTrigger id="availability-site" className="w-60">
                <SelectValue placeholder={teamsLoading ? "Loading sites…" : "Choose a site"} />
              </SelectTrigger>
              <SelectContent>
                {sites.map(team => (
                  <SelectItem key={team.id} value={team.slug!}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Schedule</Label>
            <ToggleGroup
              type="single"
              variant="outline"
              value={view}
              onValueChange={(value) => value && choose({ view: value as "site" | "host" })}
              aria-label="Schedule"
            >
              <ToggleGroupItem value="site">Site schedule</ToggleGroupItem>
              <ToggleGroupItem value="host">Host schedule</ToggleGroupItem>
            </ToggleGroup>
          </div>

          {view === "host" && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="availability-host">Host</Label>
              <Select
                value={calUserId ? String(calUserId) : ""}
                onValueChange={(id) => choose({ host: Number(id) })}
                disabled={!site}
              >
                <SelectTrigger id="availability-host" className="w-60">
                  <SelectValue placeholder={site ? "Choose a host" : "Choose a site first"} />
                </SelectTrigger>
                <SelectContent>
                  {hosts.map(host => (
                    <SelectItem key={host.id} value={String(host.id)}>
                      {host.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {target && week ? (
        <SlotPlanEditor
          key={`${target.kind}:${target.teamSlug}:${target.kind === "host" ? target.calUserId : ""}`}
          target={target}
          canManage={canManage}
          timeZone={site?.timeZone || SITE_TIME_ZONE}
          week={week}
          hostEmail={view === "host" ? selectedHost?.email ?? null : null}
          mainOffice={site?.isMainOffice ?? false}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          {!site
            ? "Choose a site to see its schedule."
            : "Choose a host to see their availability at this site."}
        </p>
      )}
    </div>
  )
}
